import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { AuthUser } from '../models/auth-user.model';
import { Group } from '../models/group.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { Authority } from '../models/authority.model';
import { map, tap } from 'rxjs/operators';
import { Page } from '../models/page.model';
import { User } from '../models/user.model';
import { MessageService } from './message.service';
import { SortParam } from '../shared/components/sort-toggle/sort-toggle.component';
import { environment } from '@environments/environment';


export interface Principal {
  id: number;
  userId: string;
  username: string;
  email: string;
  groups: Group[];
  athorities: Authority[];
}

export interface ChangePasswordRequest {
  password: string;
}

export interface EnableUserRequest {
  enabled: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private readonly baseUrl = `${environment.http.apiRoot}`;
  private currentUserSubject: BehaviorSubject<AuthUser>;
  currentUser: Observable<AuthUser>;

  readonly defaultSort: SortParam[] = [{paramName: 'username', sortDirection: 'ASC'}];

  constructor(private http: HttpClient, private messageService: MessageService) {
      this.currentUserSubject = new BehaviorSubject<AuthUser>(JSON.parse(localStorage.getItem('currentUser')));
      this.currentUser = this.currentUserSubject.asObservable().pipe(map(res => Object.assign(new AuthUser(), res)));
  }

  get currentUserValue(): AuthUser {
    return this.currentUserSubject.value;
  }

  setUserInfo() {
    return this.http.get<any>(`${this.baseUrl}/principal`).subscribe(res => {
      const authUser = this.mapAuthUser(res.principal, res.authorities);
      localStorage.setItem('currentUser', JSON.stringify(authUser));
      this.currentUserSubject.next(authUser);
      return res;
    });
  }

  unsetUserInfo() {
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
  }

  getUser(userId: number) {
    return this.http.get<User>(`${this.baseUrl}/users/${userId}`);
  }

  getUserByUsername(username: string) {
    return this.http.get<User>(`${this.baseUrl}/users/username/${username}`);
  }

  getUserList(params: HttpParams) {
    return this.http.get<Page<User>>(`${this.baseUrl}/users`, {params});
  }

  createUser(createUserRequest) {
    return this.http.post<any>(`${this.baseUrl}/users`, createUserRequest).pipe(
      tap(res => this.messageService.handleSuccess('USERS.MESSAGES.CREATE.SUCCESS'))
    );
  }

  updateUser(userId, createUserRequest) {
    return this.http.put<any>(`${this.baseUrl}/users/${userId}`, createUserRequest).pipe(
      tap(res => this.messageService.handleSuccess('USERS.MESSAGES.UPDATE.SUCCESS'))
    );
  }

  deleteUser(userId) {
    return this.http.delete<any>(`${this.baseUrl}/users/${userId}`).pipe(
      tap(res => this.messageService.handleSuccess('USERS.MESSAGES.DELETE.SUCCESS'))
    );
  }

  changeUserPassword(userId: number, changePasswordRequest: ChangePasswordRequest) {
    return this.http.put<any>(`${this.baseUrl}/users/${userId}/password`, changePasswordRequest).pipe(
      tap(res => this.messageService.handleSuccess('USERS.MESSAGES.PASSWORD_UPDATE.SUCCESS'))
    );
  }

  enableUser(userId: number, enableUserRequest: EnableUserRequest) {
    const params = new HttpParams()
      .set('enabled', `${enableUserRequest.enabled}`);

    return this.http.put<any>(`${this.baseUrl}/users/${userId}/enable`, enableUserRequest, {params}).pipe(
      tap(res => this.messageService.handleSuccess('USERS.MESSAGES.UPDATE.SUCCESS'))
    );
  }

  sendSignupEmail(userId: number) {
    return this.http.post<any>(`${this.baseUrl}/users/${userId}/signup/email`, {}).pipe(
      tap(res => this.messageService.handleSuccess('USERS.MESSAGES.SIGNUP_EMAIL.SUCCESS'))
    );
  }

  mapAuthUser(principal: Principal, authorities: Authority[]): AuthUser {
    return Object.assign(new AuthUser(), principal, {
      id: principal.id,
      userId: principal.userId,
      username: principal.username,
      email: principal.email,
      groups: principal.groups,
      authorities
    });
  }
}
